import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  GraduationCap, Cog, Rocket, ArrowRight, Check, Clock, CalendarCheck,
  KeyRound, Lock, Quote, Droplets, Utensils, Mic, Wrench, TrendingUp, Route, UserRound,
} from "lucide-react";
import { createCheckout, verifyAccessCode, getConfig } from "@/lib/api";
import { SiteNav } from "@/components/SiteNav";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const SAMPLE_QS = [
  "Can a new customer understand what you sell within 10 seconds of landing on your page?",
  "Do you have one clear offer you want people to buy, book, or request?",
  "Does your website or social page clearly tell people what to do next?",
  "Do you follow up with people who show interest but do not buy right away?",
  "Do you have a repeatable system for turning attention into customers?",
  "Are you posting content that connects directly to an offer, event, service, or product?",
];

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [payOpen, setPayOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [product, setProduct] = useState({ code: "BASIC", price: 49, name: "Leak Finder Survey + Basic Results" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ calendly_url: "", products: {} });
  const [sampleAnswers, setSampleAnswers] = useState({});

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    const id = location.state?.scrollTo;
    if (id) {
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
    }
  }, [location.state]);

  const FALLBACK = {
    BASIC: { price: 49, name: "Leak Finder Survey + Basic Results" },
    STRATEGY: { price: 97, name: "30-Minute Strategy Call" },
    BUNDLE: { price: 129, name: "Audit + Strategy Call Bundle" },
  };
  const openPay = (productCode) => {
    const p = config?.products?.[productCode] || FALLBACK[productCode];
    setProduct({ code: productCode, price: p?.price ?? 49, name: p?.name ?? "Leak Finder Survey" });
    setPayOpen(true);
  };

  const startPayment = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { url } = await createCheckout({
        buyer_email: email.trim(),
        buyer_name: name.trim(),
        product_code: product.code,
        origin_url: window.location.origin,
      });
      window.location.href = url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const submitCode = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await verifyAccessCode(code.trim());
      toast.success("Access approved. Your Leak Finder Survey is unlocked.");
      navigate("/survey", { state: { access_type: "code", access_ref: res.access_ref } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That code is invalid or expired. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  const label = "text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] text-brand-gold uppercase";

  return (
    <div className="min-h-screen bg-brand-bone text-brand-ink">
      <SiteNav onStartAudit={() => openPay("BASIC")} onAccessCode={() => setCodeOpen(true)} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/[0.04] to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 md:pt-28 md:pb-28 text-center relative">
          <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.6 }}>
            <p className={label + " mb-6"}>The 3E Business Leak Finder</p>
            <h1 className="font-heading font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl">
              <span className="text-brand-ink">Find the Leaks</span>
              <br />
              <span className="text-brand-gold">Before You Spend</span>
              <br />
              <span className="text-brand-gold">More on Marketing</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-brand-ink/70 max-w-2xl mx-auto leading-relaxed">
              The 3E Business Leak Finder identifies where your business is losing money,
              attention, customers, or consistency — through{" "}
              <strong className="text-brand-ink">Education, Engineering, and Execution</strong>.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="hero-cta-button"
                onClick={() => openPay("BASIC")}
                className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-white text-sm font-semibold uppercase tracking-wide px-8 py-6 shadow-lg shadow-brand-gold/25 hover:-translate-y-0.5 transition-all"
              >
                Get My Business Leak <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
              <Button
                data-testid="hero-preview-btn"
                variant="outline"
                onClick={() => scrollTo("sample-survey")}
                className="rounded-full border-brand-green text-brand-green hover:bg-brand-green/5 text-sm font-semibold uppercase tracking-wide px-8 py-6"
              >
                Preview the Survey First
              </Button>
            </div>

            <p className="mt-8 text-brand-ink/60 max-w-lg mx-auto">
              Find out where your business is losing money, attention, and customers.
            </p>
            <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-brand-green bg-brand-green/10 rounded-full px-4 py-1.5">
              $49 • Takes about 7–10 minutes • Basic diagnosis emailed to you
            </p>
          </motion.div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="bg-brand-green text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <p className="text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] text-brand-gold uppercase">The Problem</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-4 leading-tight">
            More marketing won't fix a leaking business.
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed">
            Most owners don't have a traffic problem. They have a leak problem — in the
            message, the customer journey, the follow-up, the systems, and the daily
            execution. Pouring more ad spend on a leaking funnel just empties your wallet
            faster.
          </p>
          <p className="mt-4 text-white/80 text-lg leading-relaxed">
            The 3E Leak Finder diagnoses exactly where the money, attention, and
            customers are slipping through.
          </p>
        </div>
      </section>

      {/* WHAT IT CHECKS */}
      <section id="what-it-checks" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <p className={label}>What It Checks</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-brand-green">
            3 categories. 30 questions.
            <br />
            One brutally honest score.
          </h2>
          <p className="mt-4 text-base sm:text-lg font-normal text-brand-green/70 leading-relaxed">
            See where your brand, customer experience, and sales process may be costing you money.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { icon: GraduationCap, title: "Education Leaks", text: "Offer clarity, customer understanding, brand message — do people actually know what you do and why it matters?" },
            { icon: Cog, title: "Engineering Leaks", text: "Website flow, booking path, customer journey, payment path — is the road from interested to paid smooth or broken?" },
            { icon: Rocket, title: "Execution Leaks", text: "Consistency, follow-up, content, sales action — are you showing up daily or starting and stopping?" },
          ].map((e, i) => (
            <motion.div
              key={e.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-xl border border-border bg-white p-8 hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green mb-5">
                <e.icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-xl font-semibold">{e.title}</h3>
              <p className="mt-3 text-brand-ink/70 leading-relaxed">{e.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border-l-4 border-brand-gold bg-white p-8 shadow-sm flex gap-4">
          <Quote className="w-8 h-8 text-brand-gold shrink-0" />
          <p className="text-lg text-brand-ink/80 leading-relaxed">
            This is not a generic survey. This is a business leak diagnostic built around
            the 3E framework: <strong className="text-brand-ink">Education, Engineering, and Execution.</strong>
          </p>
        </div>
      </section>

      {/* SAMPLE SURVEY QUESTIONS */}
      <section id="sample-survey" data-testid="sample-survey" className="bg-white border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <p className={label}>Sample Survey Questions</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4">
            Try a few. Example questions only.
          </h2>
          <p className="mt-4 text-brand-ink/70 leading-relaxed max-w-2xl">
            Rate each statement from 1 (Not in place) to 5 (Strong and consistent). These are
            sample questions — the full survey contains 30.
          </p>

          <div className="mt-6 rounded-xl bg-brand-bone border border-border p-5 grid grid-cols-1 sm:grid-cols-5 gap-3 text-sm" data-testid="sample-rating-key">
            {[["1", "Not in place"], ["2", "Needs work"], ["3", "Somewhat working"], ["4", "Working well"], ["5", "Strong and consistent"]].map(([n, t]) => (
              <div key={n} className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center text-xs font-bold shrink-0">{n}</span>
                <span className="text-brand-ink/70">{t}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 space-y-4">
            {SAMPLE_QS.map((q, i) => (
              <div key={i} className="rounded-xl border border-border bg-brand-bone p-5 md:p-6" data-testid={`sample-q-${i + 1}`}>
                <p className="font-medium text-brand-ink">
                  <span className="text-brand-gold font-semibold mr-2">Q{i + 1}</span>{q}
                </p>
                <div className="mt-4 flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = sampleAnswers[i] === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        data-testid={`sample-rate-${i + 1}-${n}`}
                        onClick={() => setSampleAnswers((a) => ({ ...a, [i]: n }))}
                        className={`w-11 h-11 rounded-lg font-heading font-bold transition-all ${
                          active
                            ? "bg-brand-green text-white ring-2 ring-brand-green"
                            : "bg-white border border-border text-brand-ink/70 hover:border-brand-green"
                        }`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-brand-ink/70 leading-relaxed max-w-2xl">
            Like what you see? The full survey scores you across all 30 questions and shows
            exactly which leaks are costing you the most.
          </p>
          <Button
            data-testid="sample-start-full"
            onClick={() => openPay("BASIC")}
            className="mt-6 rounded-full bg-brand-gold hover:bg-brand-goldDark text-white uppercase tracking-wide text-sm font-semibold px-8 py-6 shadow-lg hover:-translate-y-0.5 transition-all"
          >
            Start the Full $49 Survey
          </Button>
        </div>
      </section>

      {/* SAMPLE RESULTS PREVIEW */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <p className={label}>Sample Results Preview</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4">
          What a client might receive.
        </h2>
        <p className="mt-4 text-brand-ink/70 max-w-2xl">
          Below is an example result page. Yours will be based on your actual answers.
        </p>

        <div className="mt-8 rounded-2xl border border-border bg-white shadow-sm overflow-hidden" data-testid="sample-result-card">
          <div className="bg-brand-green text-white p-6 md:p-8 text-center">
            <p className="text-xs uppercase tracking-wide text-brand-gold font-semibold">Business Leak Score</p>
            <div className="font-heading text-5xl font-extrabold mt-2">61<span className="text-2xl text-white/50"> / 100</span></div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
            {[["Education", "6 / 10", null], ["Engineering", "4 / 10", "WEAKEST"], ["Execution", "5 / 10", null]].map(([name, score, tag]) => (
              <div key={name} className="p-5 text-center">
                <p className="text-sm text-brand-ink/60">{name}</p>
                <p className="font-heading text-2xl font-bold mt-1">{score}</p>
                {tag && <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wide text-white bg-brand-gold rounded-full px-2 py-0.5">{tag}</span>}
              </div>
            ))}
          </div>
          <div className="p-6 md:p-8 space-y-5">
            <div>
              <p className="text-xs uppercase tracking-wide text-brand-gold font-semibold">Top Leak Found</p>
              <p className="mt-2 text-brand-ink/80 leading-relaxed">
                "People may be interested in the business, but the customer journey does not
                clearly move them from attention to action."
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-brand-gold font-semibold">Recommended Next Step</p>
              <p className="mt-2 text-brand-ink/80 leading-relaxed">
                Clarify the main offer, improve the booking/payment path, and create a follow-up
                system for interested customers.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-8 text-brand-ink/70 max-w-2xl leading-relaxed">
          The full $49 survey gives you your Education, Engineering, and Execution score, your
          overall Business Leak Score, your top 3 leaks, and basic action recommendations.
        </p>
        <Button
          data-testid="sample-results-cta"
          onClick={() => openPay("BASIC")}
          className="mt-6 rounded-full bg-brand-green hover:bg-brand-greenDark text-white uppercase tracking-wide text-sm font-semibold px-8 py-6"
        >
          Get My Business Leak
        </Button>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 md:pb-12">
        <p className={label}>Who This Is For</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4">
          Built for small businesses that know they're close — but something is leaking.
        </h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-8">
          {[
            { icon: Utensils, t: "Food trucks and restaurants" },
            { icon: Mic, t: "Personal brands and creators" },
            { icon: Wrench, t: "Service businesses" },
            { icon: TrendingUp, t: "Small businesses getting attention but not enough sales" },
            { icon: Route, t: "Owners who need a clearer customer path" },
          ].map((a) => (
            <div key={a.t} className="flex items-center gap-3 rounded-xl border border-border bg-white p-4">
              <span className="w-9 h-9 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                <a.icon className="w-5 h-5" />
              </span>
              <span className="text-brand-ink/80">{a.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* STEP 1 + STEP 2 */}
      <section id="pricing" className="bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-14 items-stretch">
            {/* Desktop connector (lg+ only) — thin gold line + pill, subtle one-time fade-in */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center gap-2 pointer-events-none"
              data-testid="connector-desktop"
            >
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-brand-gold/60" />
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-gold/50 text-brand-goldDark text-xs font-semibold uppercase tracking-[0.15em] px-4 py-2 shadow-md whitespace-nowrap">
                then <ArrowRight className="w-4 h-4" />
              </span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-brand-gold/60" />
            </motion.div>
            {/* STEP 1 */}
            <div className="flex flex-col">
              <p className={label}>Step 1 — Diagnose</p>
              <div className="mt-6 flex-1 rounded-2xl border border-border bg-brand-bone p-8 md:p-10 flex flex-col">
                <span className="self-start inline-flex items-center gap-1.5 rounded-full bg-brand-green text-white text-xs font-semibold uppercase tracking-wide px-3 py-1 mb-4" data-testid="badge-basic">
                  <Check className="w-3.5 h-3.5" /> Recommended First Step
                </span>
                <div className="flex items-baseline justify-between">
                  <h3 className="font-heading text-2xl font-bold">Leak Finder Survey<br />+ Basic Results</h3>
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-heading text-5xl font-extrabold text-brand-green">$49</span>
                  <span className="text-brand-ink/50">one-time</span>
                </div>
                <p className="mt-4 text-brand-ink/70">
                  Take the 3E Leak Finder Survey and receive a basic diagnosis of where your
                  business is leaking attention, customers, and money.
                </p>
                <ul className="mt-6 space-y-3">
                  {["3E Leak Finder survey (30 questions)", "Education, Engineering, and Execution score", "Top 3 business leaks identified", "Basic next-step recommendation"].map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                      <span className="text-brand-ink/80">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 rounded-xl bg-brand-green/5 border border-brand-green/15 p-5" data-testid="what-you-get-box">
                  <p className="text-sm font-heading font-semibold text-brand-green">Your basic results will identify:</p>
                  <ul className="mt-2 space-y-1.5 text-sm text-brand-ink/70">
                    <li>• Your strongest 3E category</li>
                    <li>• Your weakest 3E category</li>
                    <li>• Your top 3 business leaks</li>
                    <li>• A basic next-step recommendation</li>
                  </ul>
                </div>
                <Button
                  data-testid="buy-basic-button"
                  onClick={() => openPay("BASIC")}
                  className="mt-8 w-full rounded-full bg-brand-gold hover:bg-brand-goldDark text-white uppercase tracking-wide text-sm font-semibold py-6"
                >
                  Buy the Leak Finder Survey
                </Button>
                <p className="mt-3 text-xs text-brand-ink/55 text-center leading-relaxed">
                  After payment, you'll be taken directly to the survey. Your basic results will
                  be emailed after completion.
                </p>
              </div>
            </div>

            {/* STEP 2 */}
            <div className="flex flex-col">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 1.1, ease: "easeOut" }}
                className="lg:hidden flex items-center justify-center gap-3 mb-5"
                data-testid="connector-mobile"
              >
                <span className="h-px w-8 bg-brand-gold/40" />
                <span className="text-sm font-semibold text-brand-goldDark whitespace-nowrap">Then upgrade when ready ↓</span>
                <span className="h-px w-8 bg-brand-gold/40" />
              </motion.div>
              <p className={label}>Step 2 — Fix</p>
              <div className="mt-6 mb-4">
                <h3 className="font-heading text-2xl font-bold">Ready to fix the leaks?</h3>
                <p className="mt-2 text-brand-ink/70">
                  Take the survey first, then book a strategy call when you're ready to turn the
                  diagnosis into a real action plan.
                </p>
                <p className="mt-4 text-brand-ink/80 font-medium border-l-2 border-brand-gold pl-4">
                  The $49 survey tells you where the leak is. The $97 Strategy Call helps you
                  decide what to fix first and how to move forward.
                </p>
              </div>

              {/* Reviewer */}
              <div className="mb-4 flex items-center gap-4 rounded-2xl border border-border bg-brand-bone p-4" data-testid="reviewer-card">
                <img
                  src="https://customer-assets.emergentagent.com/job_leak-finder-web/artifacts/9w00w9ta_ChatGPT%20Image%20Jun%2011%2C%202026%2C%2007_36_49%20PM.png"
                  alt="Frank Marina III"
                  className="w-20 h-20 rounded-xl object-cover shrink-0"
                  style={{ objectPosition: "50% 22%" }}
                  data-testid="reviewer-photo"
                />
                <div>
                  <p className="text-xs uppercase tracking-wide text-brand-gold font-semibold">Reviewed by</p>
                  <p className="font-heading text-lg font-bold text-brand-ink">Frank Marina III</p>
                  <p className="text-sm text-brand-ink/60">Tycoon Promotions | 3E Audit</p>
                  <p className="text-xs text-brand-ink/50 mt-1.5 leading-relaxed">
                    20+ years in promotions, events, customer experience, and business execution.
                  </p>
                </div>
              </div>
              <div className="flex-1 rounded-2xl bg-brand-green text-white p-8 md:p-10 flex flex-col shadow-xl">
                <span className="self-start inline-flex items-center gap-1.5 rounded-full bg-brand-gold/20 text-brand-gold text-[11px] font-semibold uppercase tracking-wide px-3 py-1 mb-4 border border-brand-gold/40" data-testid="badge-review">
                  Best After Your Survey
                </span>
                <h3 className="font-heading text-2xl font-bold">30-Minute<br />Strategy Call</h3>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-2xl text-white/50 line-through" data-testid="strategy-old-price">$149</span>
                  <span className="font-heading text-5xl font-extrabold text-brand-gold" data-testid="strategy-price">$97</span>
                  <span className="text-white/60">now</span>
                </div>
                <span className="self-start mt-2 inline-block text-[11px] font-bold uppercase tracking-wide text-brand-green bg-brand-gold rounded-full px-2 py-0.5">Save $52 · limited-time</span>
                <p className="mt-4 text-white/80">
                  Turn your leak results into a clear action plan on a live call with Frank.
                </p>
                <ul className="mt-6 space-y-3">
                  {["Personalized written breakdown", "30-minute strategy call with Frank", "3 priority fixes for your business", "Recommended next move", "Credit toward the full 3E Audit"].map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-brand-gold/20 text-brand-gold flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                      <span className="text-white/90">{b}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid="buy-strategy-button"
                  onClick={() => openPay("STRATEGY")}
                  className="mt-8 w-full rounded-full bg-brand-gold hover:bg-brand-goldDark text-white uppercase tracking-wide text-sm font-semibold py-6"
                >
                  Book My Strategy Call
                </Button>
                <p className="mt-3 text-xs text-white/70 text-center leading-relaxed">
                  After payment, you'll receive the booking link for your 30-minute strategy call.
                </p>
              </div>
            </div>
          </div>

          {/* BUNDLE — best value */}
          <div className="mt-8 rounded-2xl border-2 border-brand-gold bg-brand-bone p-8 md:p-10" data-testid="bundle-card">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <span className="inline-block text-[11px] font-bold uppercase tracking-wide text-brand-green bg-brand-gold rounded-full px-3 py-1">Best Value Bundle</span>
                <h3 className="font-heading text-2xl font-bold mt-3">Audit + Strategy Call Bundle</h3>
                <p className="mt-2 text-brand-ink/70 max-w-xl">
                  Get the full audit and the discounted strategy call together — and save.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-brand-ink/80">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" /></span>
                    $49 — 3E Leak Finder Audit
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" /></span>
                    $97 — Strategy Call <span className="text-brand-ink/50">(normally $149)</span>
                  </li>
                </ul>
              </div>
              <div className="text-center md:text-right shrink-0">
                <div className="flex items-baseline gap-2 justify-center md:justify-end">
                  <span className="text-xl text-brand-ink/40 line-through" data-testid="bundle-old-price">$146</span>
                  <span className="font-heading text-5xl font-extrabold text-brand-green" data-testid="bundle-price">$129</span>
                </div>
                <p className="text-xs text-brand-ink/50 mt-1">launch bundle price</p>
                <Button
                  data-testid="buy-bundle-button"
                  onClick={() => openPay("BUNDLE")}
                  className="mt-4 rounded-full bg-brand-gold hover:bg-brand-goldDark text-white uppercase tracking-wide text-sm font-semibold px-8 py-6"
                >
                  Take Survey + Book Strategy Call
                </Button>
                <p className="mt-3 text-xs text-brand-ink/55 max-w-xs md:ml-auto leading-relaxed">
                  Includes the audit, written results, and the discounted 30-minute strategy call.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOMEPAGE CREDIBILITY STRIP */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="rounded-2xl bg-brand-green text-white p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 shadow-xl" data-testid="home-credibility-section">
          <img
            src="https://customer-assets.emergentagent.com/job_leak-finder-web/artifacts/3l286q7f_Screenshot_20260705_225108_Gallery.jpg"
            alt="Frank Marina III"
            className="w-28 h-28 md:w-36 md:h-36 rounded-2xl object-cover shrink-0 ring-2 ring-brand-gold/40"
            style={{ objectPosition: "50% 20%" }}
            data-testid="home-credibility-photo"
          />
          <div className="text-center md:text-left">
            <p className="text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] text-brand-gold uppercase">Behind the 3E Leak Finder</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-3 leading-tight">
              Built From 20+ Years of Real-World Experience.
            </h2>
            <p className="mt-4 text-white/80 leading-relaxed">
              Frank Marina III has worked across promotions, hospitality, music marketing, live events,
              and customer-facing business operations — giving him a unique view of how businesses are
              seen, felt, and experienced by the people they serve.
            </p>
            <Button
              data-testid="home-meet-frank-btn"
              onClick={() => navigate("/about")}
              className="mt-6 rounded-full bg-brand-gold hover:bg-brand-goldDark text-white uppercase tracking-wide text-sm font-semibold px-8 py-5"
            >
              <UserRound className="w-4 h-4 mr-1.5" /> Meet Frank
            </Button>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <p className={label}>What Happens Next</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4">
          Book Your Strategy Call
        </h2>
        <p className="mt-5 text-brand-ink/70 text-lg leading-relaxed max-w-2xl">
          After you purchase the Strategy Call or the Bundle, you'll get instant access to
          Frank's booking calendar to schedule your 30-minute call.
        </p>

        <div className="mt-8 rounded-2xl border border-border bg-white p-6 md:p-8 shadow-sm">
          <div className="flex items-start gap-3">
            <CalendarCheck className="w-6 h-6 text-brand-green shrink-0 mt-0.5" />
            <p className="text-brand-ink/80">
              Strategy Calls are currently available{" "}
              <strong className="text-brand-ink">Monday through Friday between 10:00 AM and 2:00 PM Eastern Time.</strong>
            </p>
          </div>
          <div className="flex items-start gap-3 mt-4 pt-4 border-t border-border">
            <Clock className="w-6 h-6 text-brand-green shrink-0 mt-0.5" />
            <p className="text-brand-ink/80">
              30-minute sessions • last start time 1:30 PM ET • 15-minute buffer between calls.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-xl bg-brand-green/5 border border-brand-green/15 p-4 text-sm text-brand-green flex items-center gap-2">
          <Lock className="w-4 h-4" /> Calendar access is unlocked the moment payment clears.
        </div>
      </section>

      {/* ACCESS CODE */}
      <section className="bg-white border-y border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center text-brand-goldDark mx-auto mb-5">
            <KeyRound className="w-6 h-6" />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Already have an access code?</h2>
          <p className="mt-3 text-brand-ink/70 max-w-xl mx-auto">
            Approved users can enter their access code to unlock the Leak Finder Survey
            without payment.
          </p>
          <Button
            data-testid="access-code-open-btn"
            variant="outline"
            onClick={() => setCodeOpen(true)}
            className="mt-6 rounded-full border-brand-green text-brand-green hover:bg-brand-green/5 uppercase tracking-wide text-sm font-semibold px-8 py-5"
          >
            I Have an Access Code
          </Button>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-green text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <Droplets className="w-10 h-10 text-brand-gold mx-auto" />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-6">
            One audit. A clear plan. $49.
          </h2>
          <p className="mt-5 text-white/80 text-lg max-w-2xl mx-auto leading-relaxed">
            Complete the guided survey and instantly receive your basic results, including
            your top 3 leaks and where your business may be losing money, attention, or
            customers.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="final-cta-basic"
              onClick={() => openPay("BASIC")}
              className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-white text-sm font-semibold uppercase tracking-wide px-10 py-6 shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Start My $49 Audit
            </Button>
            <Button
              data-testid="final-cta-bundle"
              variant="outline"
              onClick={() => openPay("BUNDLE")}
              className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 text-sm font-semibold uppercase tracking-wide px-10 py-6"
            >
              Get the $129 Audit + Call Bundle
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-brand-bone">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-brand-ink/70">
            Powered by <strong className="text-brand-green">Tycoon Promotions</strong> and{" "}
            <strong className="text-brand-green">3E Audit</strong>
          </p>
          <p className="mt-3 text-sm text-brand-ink/50 max-w-2xl mx-auto leading-relaxed">
            The 3E Leak Finder is built to help small businesses, restaurants, food trucks,
            personal brands, creators, and service businesses find the leaks before spending
            more money on marketing.
          </p>
          <p className="mt-4 text-sm text-brand-ink/60 max-w-2xl mx-auto leading-relaxed border-t border-border pt-4" data-testid="credibility-line">
            Built by Tycoon Promotions and 3E Audit using 20+ years of real-world experience
            in promotions, events, customer experience, food service, and business execution.
          </p>
          <nav className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm" data-testid="footer-links">
            <a href="mailto:frankmarina@3eleakfinder.com" className="text-brand-ink/60 hover:text-brand-green transition-colors" data-testid="footer-contact">Contact</a>
            <a href="/privacy" className="text-brand-ink/60 hover:text-brand-green transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms" className="text-brand-ink/60 hover:text-brand-green transition-colors" data-testid="footer-terms">Terms</a>
            <a href="/refund" className="text-brand-ink/60 hover:text-brand-green transition-colors" data-testid="footer-refund">Refund Policy</a>
          </nav>
          <a href="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-1 mt-6 text-xs text-brand-ink/40 hover:text-brand-green">
            <Lock className="w-3.5 h-3.5" /> Admin
          </a>
        </div>
      </footer>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{product.name}</DialogTitle>
            <DialogDescription>
              ${product.price} one-time · Enter your details to continue to secure Stripe checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="pay-name">Your name</Label>
              <Input id="pay-name" data-testid="payment-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Owner" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-email">Email address</Label>
              <Input id="pay-email" data-testid="payment-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="payment-submit-button"
              onClick={startPayment}
              disabled={loading}
              className="w-full rounded-full bg-brand-gold hover:bg-brand-goldDark text-white"
            >
              {loading ? "Redirecting…" : `Continue to Checkout · $${product.price}`}
            </Button>
          </DialogFooter>
          <p className="text-xs text-brand-ink/50 text-center leading-relaxed" data-testid="checkout-agreement">
            By purchasing, you agree to our{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-green">Terms</a> and{" "}
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-green">Refund Policy</a>.
          </p>
        </DialogContent>
      </Dialog>

      {/* Access code dialog */}
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent data-testid="access-code-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Enter your access code</DialogTitle>
            <DialogDescription>Approved users can unlock the survey without paying.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="access-code">Access code</Label>
            <Input
              id="access-code"
              data-testid="access-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter access code"
              onKeyDown={(e) => e.key === "Enter" && submitCode()}
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="access-code-submit-button"
              onClick={submitCode}
              disabled={loading}
              className="w-full rounded-full bg-brand-green hover:bg-brand-greenDark text-white"
            >
              {loading ? "Checking…" : "Unlock Survey"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
